import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ListView, ListViewFooter, ListViewHeader, ListViewItemProps } from "@progress/kendo-react-listview";
import { SvgIcon } from "@progress/kendo-react-common";
import { Avatar } from "@progress/kendo-react-layout";
import { arrowRotateCwIcon, plusIcon, saveIcon, searchIcon, starIcon, trashIcon } from "@progress/kendo-svg-icons";
import { TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { useLocation } from "react-router-dom";
import { loadingPanel } from "../../LoadingPanel";


/**
 * ConfigDevicesManagement is a component responsible for managing the configuration
 * of devices. It interacts with an API to fetch and manipulate device data, including
 * adding, updating, and disabling devices. The component maintains various states
 * for managing device lists, selected device details, loading states, and notifications.
 * It also utilizes localization services for displaying language-specific strings.
 */

export const ConfigDevicesManagement = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    const [devicesList, setDevicesList] = useState<any>(null);
    const [counterActiveDevices, setCounterActiveDevices] = useState(null);
    const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);
    const [deviceToDisable, setDeviceToDisable] = useState<any>(null);
    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');
    const [deviceVersionSelected, setDeviceVersionSelected] = useState<TextBoxProps["value"]>('');

    const [batterySelected, setBatterySelected] = useState('');
    const [lastCommunicationTimeSelected, setLastCommunicationTimeSelected] = useState<any>('');
    const [mapIdSelected, setMapIdSelected] = useState<any>('');

    const [listViewData, setListViewData] = useState(devicesList);
    const [groupsList, setGroupsList] = useState<any>([]);
    const [groupNameSelected, setGroupNameSelected] = useState<any>(null);
    const [groupIdSelected, setGroupIdSelected] = useState<any>(null);

    const [jobsList, setJobsList] = useState<any>(null);
    const [jobSelected, setJobSelected] = useState<any>(null);
    const [currentJobSelected, setCurrentJobSelected] = useState<any>(null);
    const [jobIdSelected, setJobIdSelected] = useState<any>(null);
    const [jobsListName, setJobsListName] = useState<any>(null);

    const [messageDisabledDevice, setMessageDisabledDevice] = useState<boolean>(false);
    const [showPopupDisableDevice, setShowPopupDisableDevice] = useState<boolean>(false);

    const [messageAddedDevice, setMessageAddedDevice] = useState<boolean>(false);
    const [messageSavedDevice, setMessageSavedDevice] = useState<boolean>(false);
    const [messageUpdatingDevice, setMessageUpdatingDevice] = useState<boolean>(false);
    const [emptyNameDevice, setEmptyNameDevice] = useState<boolean>(true);

    const [error, setError] = useState('');

    const [showPopupAddDevice, setShowPopupAddDevice] = useState<boolean>(false);
    const [newDeviceMac, setNewDeviceMac] = useState<string>('');
    const [filterValue, setFilterValue] = useState("");

    const localizationService = useLocalization();

    const [instance, accessTokenRequest] = AccessToken();

    let languageId = localStorage.getItem('languageId');

    const userType = localStorage.getItem('userType');


    const [loading, setLoading] = useState(false);
    const devicesListContainerRef = useRef<HTMLDivElement | null>(null);
    const listScrollTopRef = useRef(0);

    const location = useLocation();
    // get deviceMac from synoptic grid devices
    let deviceMac = location?.state?.deviceMac;

    const getListScrollElement = () => {
        const container = devicesListContainerRef.current;
        if (!container) {
            return null;
        }

        return (container.querySelector('.k-listview-content') as HTMLElement | null) ?? container;
    };

    const rememberListScrollPosition = () => {
        const scrollElement = getListScrollElement();
        if (!scrollElement) {
            return;
        }

        listScrollTopRef.current = scrollElement.scrollTop;
    };

    const restoreListScrollPosition = () => {
        const savedScrollTop = listScrollTopRef.current;

        const applyScroll = () => {
            const currentScrollElement = getListScrollElement();
            if (!currentScrollElement) {
                return;
            }

            currentScrollElement.scrollTop = savedScrollTop;
        };

        applyScroll();
        requestAnimationFrame(() => {
            applyScroll();

            // First selection also changes layout; apply twice to avoid initial jump-to-top.
            requestAnimationFrame(() => {
                applyScroll();
            });
        });
    };

    const getDeviceList = async (flag: boolean) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res: any) => {

                    if (res == '' || res?.data?.length == 0) {
                        setLoading(false);
                        return
                    }
                    if (res.length == 0) {
                        setLoading(false);
                        return
                    }

                    const data: any = res?.data
                    if (flag) {
                        setCounterActiveDevices(data?.data?.length);
                        setDevicesList(data?.data);
                    } else {
                        setCounterInactiveDevices(data?.data?.length);
                    }
                    setListViewData(data?.data);
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
    }, [])


    const onFilterChange = (ev: any) => {
        let value = ev.value;
        setFilterValue(value);
        const newData = devicesList.filter((device: any) => {
            return (
                device.name?.toLowerCase().includes(value.toLowerCase()) ||
                device.mac?.toLowerCase().includes(value.toLowerCase())
            );
        });
        setListViewData(newData);
    };

    const clearFilter = () => {
        setFilterValue("");          // azzera il campo input
        getDeviceList(true);
    };

    const MyHeader = () => {
        return (
            <>
                <ListViewHeader
                    style={{ color: "#018CA5", fontSize: 14 }}
                    className="pl-3 pb-2 pt-2"
                >
                    {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT FOUND')} ({counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')})
                </ListViewHeader>
            </>
        );
    };

    const MyFooter = () => {
        let messages = 0;
        devicesList.map((i: any) => {
            messages = messages + i.messages;
        });
        return (
            <ListViewFooter
                style={{ color: "#018CA5", fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
            </ListViewFooter>
        );
    };

    const toggleDialog = (item?: any) => {
        setDeviceToDisable(item);
        setShowPopupDisableDevice(!showPopupDisableDevice);
    };

    const disableDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.put(`${SERVER_URL}/device/${deviceToDisable.mac}/false`, null, { headers })
                .then(() => {
                    getDeviceList(true)
                    setDeviceMacSelected('');
                    setMessageDisabledDevice(true);
                    if (!messageAddedDevice) {
                        setTimeout(() => {
                            setMessageDisabledDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        onToggleMessage()
    }

    // called on click device
    const getDeviceMacSelected = (device: any) => {
        rememberListScrollPosition();
        setDeviceMacSelected(device.mac);
        setDeviceNameSelected(device.name);
        setDeviceTypeName(device.deviceTypeName);
        setDeviceVersionSelected(device.version);
        setGroupNameSelected(device.groupName);
        setGroupIdSelected(device.groupId);
        setJobIdSelected(device.jobId);

        setBatterySelected(device.battery);
        setLastCommunicationTimeSelected(device.lastCommunicationTime);
        setMapIdSelected(device.mapId);

        getGroupList();
        getJobsByGroup(device);
    }

    useEffect(() => {
        if (deviceMacSelected) {
            restoreListScrollPosition();
        }
    }, [deviceMacSelected]);

    const DevicesRender = (props: ListViewItemProps) => {
        let device = props.dataItem;
        return (
            <div
                className="k-listview-item row p-2 border-bottom align-middle"
                style={{ margin: 0, boxShadow: 'none' }}
            >
                <div
                    className="col-2"
                    style={{ 
                        alignContent: 'center',
                        pointerEvents: `${userType?.startsWith('UbiquicomAdmin') ? 'auto' : 'none'}`,
                        opacity: `${userType?.startsWith('UbiquicomAdmin') ? 1 : 0.5}`
                    }}
                    onClick={() => { toggleDialog(device) }}
                >
                    <Avatar
                        type="img"
                        style={{ cursor: 'pointer' }}
                        className="trash-icon"
                    >
                        <SvgIcon icon={trashIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                    </Avatar>
                </div>
                <div
                    className="col-9"
                    style={{ cursor: 'pointer', color: "#018CA5", }}
                    onClick={(e) => {
                        getDeviceMacSelected(device);
                        const title = e.currentTarget.querySelector('.device-management-name-h2') as HTMLElement | null;
                        title?.focus({ preventScroll: true });
                    }}
                >
                    <h2
                        className={`device-management-name-h2${deviceMacSelected === device.mac ? ' is-selected' : ''}`}
                        tabIndex={0}
                        style={{
                            fontSize: 16,
                            cursor: 'pointer',
                            padding: '10px'

                        }}
                    >
                        {device.name} ({device.mac})
                    </h2>
                </div>
            </div>
        );
    };

    const handleChangeDeviceName = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNameSelected(event.target.value);
        setEmptyNameDevice(false);
    }, []);

    const getGroupList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/list?enabled=true`, { headers })
                .then((res) => {
                    const data = res.data.data

                    // add no group to the list
                    data.unshift({
                        id: '-1',
                        name: localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND')
                    })

                    setGroupsList(data);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getDeviceDetailsFromSynoptic = async (deviceMac: any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers
            })
                .then((res) => {
                    const data: any = res?.data.data

                    const currentDevice = data.filter((device: any) => device?.mac === deviceMac);
                    setDeviceMacSelected(currentDevice[0].mac);
                    setDeviceNameSelected(currentDevice[0].name);
                    setDeviceTypeName(currentDevice[0].deviceTypeName);
                    setDeviceVersionSelected(currentDevice[0].version);
                    setGroupNameSelected(currentDevice[0].groupName);
                    setGroupIdSelected(currentDevice[0].groupId);
                    setBatterySelected(currentDevice[0].battery);
                    setLastCommunicationTimeSelected(currentDevice[0].lastCommunicationTime);
                    setMapIdSelected(currentDevice[0].mapId);

                    getJobsByGroupId(currentDevice[0].groupId);
                    getDeviceByMac(currentDevice[0].mac, true);
                    getGroupList();

                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getDeviceDetailsFromSynoptic(deviceMac);
    }, [deviceMac])

    useEffect(() => {
        getJobList(true);
    }, []);

    const getJobList = async (flag: boolean) => {

        setLoading(true);
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/job/list?enabled=${flag}`, { headers })
                .then((res) => {
                    const data = res?.data?.data
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    let jobList: any = []
                    data.forEach((job: any) => {
                        jobList.push(job)
                    });
                    setJobsList(jobList);
                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    };

    const getDeviceByMac = async (mac: any, active: any) => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.get(`${SERVER_URL}/Device/${mac}`, { headers })
                .then((res) => {
                    const data = res.data.data

                    if (data.groupId == 0) {
                        setGroupNameSelected(localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND'));
                    }

                    let selectedJob: any = {
                        id: data.jobId,
                        name: data.jobName
                    }
                    if (!data.jobId || !data.jobName) {
                        selectedJob = {
                            id: '-1',
                            name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND')
                        }
                    }
                    setJobSelected(selectedJob);
                    setCurrentJobSelected(selectedJob);
                    setJobIdSelected(selectedJob.id);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroup = async (device: any) => {

        if (device.groupId == -1 || device.groupId == 0) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${device.groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    setJobSelected([])
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    getDeviceByMac(device.mac, true);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroupId = async (groupId: any, deviceMacSelected?: any) => {

        if (groupId == -1) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const handleChangeGroup = (group: any) => {
        setGroupIdSelected(group.id);
        setGroupNameSelected(group.name);

        getJobsByGroupId(group.id, deviceMacSelected);
        setJobSelected(-1)
        setJobIdSelected(-1)
    }


    const handleChangeJob = useCallback((event: DropDownListChangeEvent, jobsListName: any) => {
        const currentJobSelected = event.value
        setJobSelected(currentJobSelected);
        setJobIdSelected(currentJobSelected.id)
    }, [])

    const CustomTitle = () => {
        return (
            <div
                className="custom-title"
                style={{ fontSize: "18px", lineHeight: "1.3em" }}
            >
                {localizationService.toLanguageString('configuration.devices.disable_device', 'NOT FOUND')} "{deviceToDisable.name} ({deviceToDisable.mac})" ?
            </div>
        );
    };

    const onToggleMessage = () => {
        setMessageDisabledDevice(true);
        if (!messageDisabledDevice) {
            setTimeout(() => {
                setMessageDisabledDevice(false);
            }, 3000);
        }
    };

    const openPopupAddDevice = () => {
        setShowPopupAddDevice(!showPopupAddDevice);
    }

    const addDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.post(`${SERVER_URL}/device/${newDeviceMac}`, null, { headers })
                .then(() => {
                    setDeviceNameSelected('');
                    setDeviceVersionSelected('');
                    setGroupNameSelected('');
                    setGroupIdSelected('');
                    setMessageAddedDevice(true);
                    getDeviceList(true)
                    getGroupList()
                    setDeviceMacSelected(newDeviceMac)
                    

                    setMessageSavedDevice(true);
                    if (!messageSavedDevice) {
                        setTimeout(() => {
                            setMessageSavedDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!messageAddedDevice) {
            setTimeout(() => {
                setMessageAddedDevice(false);
            }, 3000);
        }

    }

    const updateDevice = async () => {

        setLoading(true);

        const normalizedGroupId = Number(groupIdSelected);
        const normalizedJobId = Number(jobIdSelected);
        const fallbackJobId = Number(jobSelected?.id ?? currentJobSelected?.id);

        const resolvedJobId = Number.isFinite(normalizedJobId)
            ? normalizedJobId
            : Number.isFinite(fallbackJobId)
                ? fallbackJobId
                : null;

        const params: any = {
            name: deviceNameSelected,
            version: deviceVersionSelected,
            groupId: normalizedGroupId > 0 ? normalizedGroupId : null,
        };

        if (normalizedGroupId === -1) {
            params.jobId = null;
        } else if (resolvedJobId !== null && resolvedJobId > 0) {
            params.jobId = resolvedJobId;
        } else if (resolvedJobId === -1) {
            params.jobId = null;
        }


        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.put(`${SERVER_URL}/device/${deviceMacSelected}`, params, { headers })
                setMessageUpdatingDevice(true);

                clearFilter()

                if (!messageUpdatingDevice) {
                    setTimeout(() => {
                        setMessageUpdatingDevice(false);
                    }, 3000);
                }

                setLoading(false);
            } catch (error: any) {
                setError(error);
                setLoading(false);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }

    }

    return (
        <>
            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == null &&
                // Mostra un messaggio o un altro contenuto se non ci sono dati
                <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            {devicesList?.length > 0 &&
                <div className="flex-parent-element" 
                    style={{overflow: 'hidden'}}    
                >

                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {messageDisabledDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageDisabledDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.device_disabled', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageAddedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageAddedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.added_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageSavedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageSavedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.saved_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageUpdatingDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageUpdatingDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.updated_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div
                        className="flex-left-child-element"
                        ref={devicesListContainerRef}
                        style={{ width: "42%", background: "#F7F7F7" }}
                    >
                        {loading && loadingPanel}
                        <div
                            className="k-listview-item row p-2 align-middle"
                            style={{ display: 'block', margin: 0 }}
                        >
                            <fieldset style={{ display: 'flex' }} >
                                <TextBox
                                    style={{
                                        border: '2px solid #ccc',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    value={filterValue}
                                    onChange={onFilterChange}
                                    placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: '20px -5px 0px 5px' }} />
                                    )}
                                />

                                {/* refresh device list button */}
                                <Button
                                    className="plusParam"
                                    style={{ marginTop: '0px' }}
                                    onClick={() => { getDeviceList(true) }}
                                    title={localizationService.toLanguageString('configuration.devices.refresh_list', 'NOT FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon icon={arrowRotateCwIcon} style={{ margin: '8px' }} />
                                    </Avatar>
                                </Button>

                                {/* add device button */}
                                <Button
                                    themeColor={"primary"}
                                    className="plusParam"
                                    onClick={() => { openPopupAddDevice() }}
                                    style={{ marginTop: '0px' }}
                                    title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon
                                            icon={plusIcon}
                                            style={{
                                                margin: '8px'
                                            }}
                                        />
                                    </Avatar>
                                </Button>
                            </fieldset>
                        </div>

                        <ListView
                            data={listViewData ?? devicesList}
                            item={DevicesRender}
                            style={{ background: "#F7F7F7", height: '90%' }}
                            header={MyHeader}
                            footer={MyFooter}
                        />
                        {showPopupAddDevice && (
                            <Dialog
                                title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT FOUND')}
                                onClose={openPopupAddDevice}
                                contentStyle={{ color: 'black' }}
                            >
                                <TextBox
                                    style={{
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    onChange={(e: any) => setNewDeviceMac(e.target.value)}
                                    placeholder={localizationService.toLanguageString('configuration.devices.mac', 'NOT FOUND')}
                                />
                                <div className='k-window-content k-buttons-container' >
                                    <button
                                        className="add-device-button"
                                        onClick={openPopupAddDevice}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT FOUND')}
                                    </button>
                                    <button
                                        className="add-device-button"
                                        onClick={() => {
                                            if (!showPopupDisableDevice) {
                                                openPopupAddDevice()
                                                addDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                        {showPopupDisableDevice && (
                            <Dialog
                                title={<CustomTitle />}
                                onClose={toggleDialog}
                            >
                                <div>
                                    <button
                                        className="disable-device-button"
                                        onClick={toggleDialog}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT FOUND')}
                                    </button>
                                    <button
                                        className="disable-device-button"
                                        onClick={() => {
                                            if (showPopupDisableDevice) {
                                                toggleDialog()
                                                disableDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                    </div>

                    {/* right panel */}
                    {deviceMacSelected != '' &&
                        <div
                            style={{ 
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                alignItems: 'center'
                            }}
                        >
                            {/* mac + name */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <h1 className="k-h2" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}</h1>
                                    <h5 className="k-h5" style={{ color: '#018CA5' }}>{deviceMacSelected != '' ? deviceMacSelected : localizationService.toLanguageString('configuration.devices.no_mac', 'NOT_FOUND')}</h5>

                                    <hr style={{ border: '1px solid #018CA5', width: '100%' }} />

                                    <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}</div>
                                    <TextBox
                                        value={deviceNameSelected ?? ''}
                                        onChange={handleChangeDeviceName}
                                        placeholder={localizationService.toLanguageString('configuration.devices.enter_device_name', 'NOT_FOUND')}
                                    />
                                </fieldset>
                            </div>

                            <br />

                            {/* type */}
                            <div className="k-listview-item row p-1 align-middle">
                                <div className="col-2" >
                                    <Avatar type="img" style={{ background: "#018CA5" }}>
                                        <SvgIcon icon={starIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                                    </Avatar>
                                </div>
                                <div className="col-9" >
                                    <h1
                                        style={{
                                            fontSize: 16,
                                            color: "#018CA5",
                                            verticalAlign: 'bottom',
                                            display: 'inline-flex'
                                        }}
                                        className="text-uppercase"
                                    >
                                        {deviceTypeName != '' ? deviceTypeName : ''}
                                    </h1>
                                </div>
                            </div>

                            <br />

                            {/* version */}
                            {deviceVersionSelected != '' &&
                                <div
                                    className="k-listview-item row p-1 align-middle"
                                    style={{ display: 'block', margin: 0, cursor: 'default' }}
                                >
                                    <fieldset>
                                        <div className="k-h5" style={{ display: 'flex', alignItems: 'baseline', color: '#018CA5' }}>
                                            <span style={{ marginRight: '10px'}}>{localizationService.toLanguageString('configuration.devices.version', 'NOT_FOUND')}:</span>
                                            <p>{deviceVersionSelected ?? ''}</p>
                                        </div>                                        
                                    </fieldset>
                                </div>
                            }
                           
                            {/* groups */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <div className="k-h5" style={{ color: '#018CA5', marginTop: '10px' }}>{localizationService.toLanguageString('configuration.devices.groups', 'NOT_FOUND')}</div>
                                    <DropDownList
                                        data={groupsList}
                                        textField="name"
                                        dataItemKey="id"
                                        value={groupsList.find((g:any) => g.id === groupIdSelected) || null}
                                        onChange={(e:any) => handleChangeGroup(e.value)}
                                        style={{ width: "100%", color: '#018CA5', cursor: 'pointer' }}
                                    />
                                </fieldset>
                            </div>

                            {/* jobs */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                {groupIdSelected != 0 && groupIdSelected != -1 && jobsListName?.length > 0 &&
                                    <fieldset>
                                        <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.jobs', 'NOT_FOUND')}</div>
                                        <DropDownList
                                            data={jobsListName ?? []}
                                            dataItemKey="id"
                                            defaultValue={jobsListName[0]}
                                            value={!jobSelected?.name ? jobsListName[0] : jobSelected}
                                            textField={'name'}
                                            onChange={(e: any) => handleChangeJob(e, jobsListName)}
                                            style={{ width: "100%", color: '#018CA5', cursor: 'pointer' }}
                                        />
                                    </fieldset>
                                }

                                {/*  save button */}
                                <div className="k-listview-item row p-3">
                                    <Button
                                        className="save-devices-management-button"
                                        themeColor={"primary"}
                                        svgIcon={saveIcon}
                                        onClick={() => updateDevice()}
                                    >
                                        {localizationService.toLanguageString('configuration.devices.save', 'NOT_FOUND')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </>
    )
}